import { useState, useRef, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

// pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
//   "pdfjs-dist/build/pdf.worker.min.mjs",
//   import.meta.url
// ).toString();

pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export const usePdfToImage = (props = {}) => {
  const [images, setImages] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [renderProgress, setRenderProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const pdfTaskRef = useRef(null);
  const taskIdRef = useRef(0);

  const convertPdfToImage = useCallback(async (url) => {
    const taskId = ++taskIdRef.current; // unique ID per load
    setIsLoading(true);
    setImages([]);
    setDownloadProgress(0);
    setRenderProgress(0);

    try {
      // Handle image URLs directly
      if (["jpeg", "jpg", "png"].includes(props.format)) {
        setImages([{ page: 0, url }]);
        setIsLoading(false);
        return;
      }

      // Cleanup previous PDF
      if (pdfTaskRef.current) {
        try {
          await pdfTaskRef.current.destroy();
        } catch (e) {
          console.warn("Previous PDF task cleanup failed:", e);
        }
        pdfTaskRef.current = null;
      }

      // Load PDF
      const loadingTask = pdfjsLib.getDocument({url:  url});
      pdfTaskRef.current = loadingTask;

      loadingTask.onProgress = (progressData) => {
        if (progressData.total) {
          const percent = (progressData.loaded / progressData.total) * 100;
          setDownloadProgress(Math.ceil(percent));
          if (typeof props.onDownloadProgress === "function") {
            props.onDownloadProgress(Math.ceil(percent));
          }
        }
      };

      const document = await loadingTask.promise;
      const totalPages = document.numPages;
      const batchSize = props.batchSize || 5;
      const delay = props.delayBetweenBatches || 80;
      let renderedPages = 0;
      const imageList = [];

      // Render in batches
      for (let i = 1; i <= totalPages; i += batchSize) {
        // stop rendering if new task started
        if (taskId !== taskIdRef.current) {
          console.info("Cancelled previous rendering task.");
          return;
        }

        const currentBatch = [];
        for (let j = i; j < i + batchSize && j <= totalPages; j++) {
          currentBatch.push(renderPageToImage(document, j));
        }

        const batchImages = await Promise.all(currentBatch);
        batchImages.forEach((img) => {
          imageList.push(img);
          setImages((prev) => [...prev, img]);
        });

        renderedPages += batchImages.length;
        const renderPercent = Math.round((renderedPages / totalPages) * 100);
        setRenderProgress(renderPercent);

        if (typeof props.onRenderProgress === "function") {
          props.onRenderProgress(renderedPages, totalPages, renderPercent);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      if (typeof props.onAfterLoad === "function") {
        props.onAfterLoad(imageList);
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Error while converting PDF to images:", err);
      setIsLoading(false);
    }
  }, [props]);

  const cancelCurrentTask = useCallback(() => {
    taskIdRef.current++;
    if (pdfTaskRef.current) {
      try {
        pdfTaskRef.current.destroy();
      } catch (err) {
        console.warn("Error cancelling current task:", err);
      }
    }
    setIsLoading(false);
  }, []);

  return {
    imageList: images,
    isLoading,
    downloadProgress,
    renderProgress,
    convertPdfToImage,
    cancelCurrentTask,
  };
};

// Helper: Render single page to image
const renderPageToImage = async (pdf, pageNumber) => {
  const page = await pdf.getPage(pageNumber);

  const scale = 1; // logical zoom
  const viewport = page.getViewport({ scale });

  // Important: physical pixel scaling for crisp text
  const outputScale = window.devicePixelRatio  || 1;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = viewport.width * outputScale;
  canvas.height = viewport.height * outputScale;

  // Prevent squishing by matching CSS size exactly
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;

  const transform = outputScale !== 1
    ? [outputScale, 0, 0, outputScale, 0, 0]
    : null;

  await page.render({
    canvasContext: context,
    viewport,
    transform,
  }).promise;

  const url = canvas.toDataURL("image/png", 1.0);

  console.log({ page: pageNumber, url, width: viewport.width, height: viewport.height })

  return { page: pageNumber, url, width: viewport.width, height: viewport.height };
};
